import { MessageSquare, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { Paged } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useMeetings } from './api'
import { MeetingFileSettingsButton } from './components/MeetingFileSettingsModal'
import { MinutesTemplateButton } from './components/MinutesTemplateModal'
import { MeetingsTable } from './components/MeetingsTable'
import { MEETING_FILE_PERMS, MINUTES_PERMS } from './meetingFiles'
import { CHANNELS, RESULTS, type Channel, type MeetingResult } from './types'
import { useCrm } from './ui'

const SCOPES = ['all', 'upcoming', 'overdue'] as const

/** Tüm müşterilerin görüşmeleri; satıra tıklanınca müşterinin görüşme sekmesi ilgili kayıtla açılır. */
export function MeetingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const meetings = useMeetings()
  const { can } = useAuth()
  const { staff, customerName } = useCrm()
  const [q, setQ] = useState('')
  const [staffId, setStaffId] = useState('all')
  const [channel, setChannel] = useState<Channel | 'all'>('all')
  const [result, setResult] = useState<MeetingResult | 'all'>('all')
  const [scope, setScope] = useState<(typeof SCOPES)[number]>('all')
  const today = new Date().toISOString().slice(0, 10)
  const term = q.trim().toLocaleLowerCase('tr')
  const rows = (meetings.data ?? []).filter(
    (m) =>
      (staffId === 'all' || m.staffId === staffId) &&
      (channel === 'all' || m.channel === channel) &&
      (result === 'all' || m.result === result) &&
      (scope === 'all' || (scope === 'upcoming' ? !!m.nextAction && m.nextAction.date >= today : !!m.nextAction && m.nextAction.date < today)) &&
      (!term || `${customerName(m.customerId)} ${m.summary}`.toLocaleLowerCase('tr').includes(term)),
  )
  const sel = <V extends string>(id: string, label: string, value: V, onChange: (v: V) => void, options: { value: V; label: string }[]) => (
    <div className="min-w-0 space-y-1">
      <label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </label>
      <Select id={id} size="sm" value={value} onChange={onChange} options={options} />
    </div>
  )

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.meetingsPage.title')}
        description={t('crm.meetingsPage.subtitle')}
        actions={
          <>
            {can(MINUTES_PERMS.template) ? <MinutesTemplateButton /> : null}
            {can(MEETING_FILE_PERMS.settings) ? <MeetingFileSettingsButton /> : null}
          </>
        }
      />
      <section className="rounded-xl border bg-card shadow-card">
        <div className="grid grid-cols-2 gap-2 border-b p-3 lg:grid-cols-[minmax(0,1.4fr)_repeat(4,minmax(0,1fr))] lg:items-end">
          <div className="relative col-span-2 lg:col-span-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('crm.meetingsPage.search')} aria-label={t('crm.meetingsPage.search')} className="h-8 pl-8" />
          </div>
          {sel('gm-staff', t('crm.fields.staff'), staffId, setStaffId, [{ value: 'all', label: t('common.all') }, ...staff.map((s) => ({ value: s.id, label: s.name }))])}
          {sel('gm-channel', t('crm.fields.channel'), channel, setChannel, [{ value: 'all', label: t('common.all') }, ...CHANNELS.map((c) => ({ value: c, label: t(`crm.channel.${c}`) }))])}
          {sel('gm-result', t('crm.fields.result'), result, setResult, [{ value: 'all', label: t('common.all') }, ...RESULTS.map((r) => ({ value: r, label: t(`crm.result.${r}`) }))])}
          {sel('gm-scope', t('crm.fields.nextAction'), scope, setScope, SCOPES.map((s) => ({ value: s, label: t(`crm.meetingsPage.scope.${s}`) })))}
        </div>
        {meetings.isError ? (
          <ErrorState error={meetings.error} onRetry={() => void meetings.refetch()} />
        ) : meetings.isLoading ? (
          <Skeleton className="m-4 h-64" />
        ) : rows.length === 0 ? (
          <EmptyState icon={MessageSquare} title={t('crm.meeting.empty')} />
        ) : (
          <Paged rows={rows} resetKey={[q, staffId, channel, result, scope]}>
            {(page) => <MeetingsTable rows={page} showCustomer onSelect={(m) => navigate(`/customers/${m.customerId}/meetings?m=${m.id}`)} />}
          </Paged>
        )}
      </section>
    </>
  )
}
